import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import HashTag from "../components/HashTag";
import NoteCard from "../components/NoteCard";
import { blocks, blockStats, notes, tagCounts } from "../data/notes";

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" },
  }),
};

const pageTitleClass =
  "font-display text-3xl md:text-4xl font-semibold text-foreground border-l-2 border-accent pl-4";

function BlockCard({ block, idx }) {
  const { count, hasNew } = blockStats(block.slug);
  return (
    <motion.div variants={fade} custom={idx} className="h-full">
      <Link
        to={`/projects/${block.slug}`}
        className="group flex h-full flex-col rounded-lg border border-border bg-surface/80 p-6 transition-colors hover:border-accent/40 hover:bg-surface"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="font-display text-xl font-semibold text-foreground group-hover:text-accent transition-colors">
            {block.title}
          </h2>
          {hasNew && (
            <span className="shrink-0 mt-1 px-2.5 py-0.5 rounded-full bg-accent/10 text-accent text-[0.7rem] tracking-wide uppercase border border-accent/20">
              New
            </span>
          )}
        </div>
        <p className="text-muted text-sm leading-relaxed mb-5 flex-1">{block.blurb}</p>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs uppercase tracking-wide">
            {count} {count === 1 ? "note" : "notes"}
          </span>
          <span className="text-accent text-sm inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            Open <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Projects() {
  // Newest first: notes carrying an `added` date sort ahead of undated ones,
  // which keep their declaration order.
  const recent = notes
    .filter((n) => n.isNew)
    .slice()
    .sort((a, b) => (b.added || "").localeCompare(a.added || ""))
    .slice(0, 3);
  const tags = tagCounts();

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className="py-10 md:py-14 px-4 md:px-8"
    >
      <Helmet>
        <title>Projects | Soham Desai</title>
        <meta
          name="description"
          content="A research notebook organized into blocks, radar, underwater comms, satellite comms, GNSS, whales, and LEO NTN, cross-referenced by hashtags."
        />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <motion.h1 variants={fade} className={`${pageTitleClass} mb-4`}>
          Projects
        </motion.h1>
        <motion.p
          variants={fade}
          className="text-muted-foreground mb-10 max-w-3xl leading-relaxed"
        >
          A research notebook organized into blocks. Open a block to read its notes, or
          follow a <span className="text-accent">#hashtag</span> to jump between related
          notes across blocks.
        </motion.p>

        {/* Blocks */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {blocks.map((block, idx) => (
            <BlockCard key={block.slug} block={block} idx={idx} />
          ))}
        </div>

        {/* Recently added */}
        {recent.length > 0 && (
          <section className="mt-16">
            <motion.h2
              variants={fade}
              className="font-display text-2xl font-semibold text-foreground mb-6"
            >
              Recently added
            </motion.h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((note) => (
                <NoteCard key={note.slug} note={note} showBlock />
              ))}
            </div>
          </section>
        )}

        {/* Tag cloud */}
        {tags.length > 0 && (
          <section className="mt-16">
            <motion.h2
              variants={fade}
              className="font-display text-2xl font-semibold text-foreground mb-2"
            >
              Cross-references
            </motion.h2>
            <motion.p variants={fade} className="text-muted-foreground text-sm mb-6">
              Every hashtag links to all notes that share it.
            </motion.p>
            <motion.div variants={fade} className="flex flex-wrap gap-2">
              {tags.map(([tag, count]) => (
                <span key={tag} className="inline-flex items-center">
                  <HashTag tag={tag} variant="chip" />
                  <span className="ml-1 mr-1 text-muted-foreground text-xs">{count}</span>
                </span>
              ))}
            </motion.div>
          </section>
        )}
      </div>
    </motion.div>
  );
}
